import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the bulk-discount progress banner in step with the cart.
 *
 * The banner counts ropes server-side, so an AJAX add-to-cart from the collection grid would
 * otherwise leave it asserting "add 4 more ropes" immediately after a rope was added - stale at
 * exactly the moment the message matters. On every cart change the whole section is re-rendered
 * and morphed back in, which keeps the counting logic in Liquid and leaves nothing to drift.
 *
 * Caching is disabled on the render: a cached section is by definition the pre-update cart.
 *
 * @extends {Component<{}>}
 */
class BulkDiscountBannerComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Refreshes once the cart request itself has resolved, so the re-render sees the new cart
   * rather than racing it.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise?.then(() => this.#refresh()).catch(this.#warn);
  };

  async #refresh() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      this.#warn(error);
    }
  }

  /**
   * @param {unknown} error
   */
  #warn = (error) => {
    if (error instanceof Error && error.name === 'AbortError') return;
    console.warn('[bulk-discount-banner] Could not refresh after cart update:', error);
  };
}

if (!customElements.get('bulk-discount-banner')) {
  customElements.define('bulk-discount-banner', BulkDiscountBannerComponent);
}
